import { useEffect, useState, lazy, Suspense } from "react";
import Hero from "@/components/Hero";
import HomeQuickNav from "@/components/HomeQuickNav";
import HomeAppPromoCard from "@/components/HomeAppPromoCard";
import ProfessorOfTheDayStrip from "@/components/ProfessorOfTheDayStrip";
import EpisodeCard from "@/components/EpisodeCard";
import EpisodeCardSkeleton from "@/components/EpisodeCardSkeleton";
import LazyMount from "@/components/LazyMount";
// Below-the-fold heavy modules are code-split so the first paint only
// downloads Hero + cards JS. RankingSection, EditorialIntro, BlogTeaser
// and AboutShareBlock previously shipped in the eager home bundle —
// they're never visible on the initial viewport, so paying for their
// JS+CSS upfront delays LCP. Lazy() splits each into its own chunk.
const RankingSection      = lazy(() => import("@/components/RankingSection"));
const EditorialIntro      = lazy(() => import("@/components/EditorialIntro"));
const PodcastsShowcase    = lazy(() => import("@/components/PodcastsShowcase"));
const PromoVideosShowcase = lazy(() => import("@/components/PromoVideosShowcase"));
const BlogTeaser          = lazy(() => import("@/components/BlogTeaser"));
const HomeLegalNewsStrip = lazy(() => import("@/components/HomeLegalNewsStrip"));
const HomeLegalActsStrip = lazy(() => import("@/components/HomeLegalActsStrip"));
const HomeConcursosAbertosStrip = lazy(() => import("@/components/HomeConcursosAbertosStrip"));
const HomeRadarShowcase  = lazy(() => import("@/components/HomeRadarShowcase"));
const HomeCommunitySpotlight = lazy(() => import("@/components/HomeCommunitySpotlight"));
const HomeScientificRankingStrip = lazy(() => import("@/components/HomeScientificRankingStrip"));
const AboutShareBlock     = lazy(() => import("@/components/AboutShareBlock"));
// CreatorOfTheMonth is currently hidden on the home page (see render block).
// Keep the import for easy re-enabling in ~6 months.
// eslint-disable-next-line no-unused-vars
const CreatorOfTheMonth   = lazy(() => import("@/components/CreatorOfTheMonth"));
// Iter295.13 — halo âmbar seguindo o mouse (só na Home, só desktop).
// Lazy: só entra no bundle da Home, e o próprio componente aborta
// silenciosamente em touch/prefers-reduced-motion.
const MouseHaloCursor = lazy(() => import("@/components/MouseHaloCursor"));
import SEO from "@/components/SEO";
import StructuredData from "@/components/StructuredData";
import { api } from "@/lib/api";
import { reportError } from "@/lib/errors";
import { Flame, Youtube, Library, Users, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

// EPISODES_PER_SHELF was a hack to limit how many cards were rendered
// per podcast row. We've since moved per-podcast episode lists OFF the
// homepage (heavy redundancy with "Últimos episódios" and "Ranking" —
// JurisTube publishes ~2 videos a week, so the same titles surfaced 3x).
// The homepage now shows a compact PodcastsShowcase grid (covers + name
// + description + episode-count link). Full shelves live on /podcast/:slug.

export default function HomePage() {
  const [podcasts, setPodcasts] = useState([]);
  const [latest, setLatest] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [pAll, s, a] = await Promise.all([
          api.listPodcasts(),
          api.stats(),
          // Home only renders 8 author cards — fetching the full 200-record
          // list was ~36 KB of JSON parsed for nothing. Total count is
          // read from stats.authors and shown in the "Ver todos os N" CTA.
          // ``rotate=daily`` cycles the 8 names every UTC day so every
          // author lands on the home roughly every ~25 days (with ~200
          // names) — fixes the long-standing visibility bias against
          // late-alphabet authors. Cost: same Mongo aggregation, ~50KB
          // extra over the wire vs ``limit=8`` alone; negligible against
          // the existing parallel ``stats``+``listPodcasts`` payloads.
          api.listAuthors({ limit: 8, rotate: "daily" }),
        ]);
        // A grade "Nossos podcasts" lista só os podcasts editoriais
        // públicos com ao menos 1 episódio visível. A coleção
        // institucional ("Vídeos de divulgação") saiu daqui e ganhou o
        // segmento próprio <PromoVideosShowcase/> logo abaixo.
        const visiblePodcasts = (pAll || []).filter(
          (p) => !p.hidden_from_lists && (p.episode_count || 0) > 0,
        );
        setPodcasts(visiblePodcasts);
        setStats(s);
        setAuthors(a);
        setLatest(s.latest || []);
        // PodcastsShowcase only needs metadata (cover/name/description/
        // episode_count) which already comes from listPodcasts — no
        // per-podcast episode fetches needed on the homepage anymore.
      } catch (e) {
        reportError(e, { component: "HomePage", phase: "load" });
      }
    })();
  }, []);

  const totalEpisodes = stats?.episodes || 0;

  return (
    <>
      <SEO url="/" />
      <StructuredData type="WebSite" />
      <StructuredData type="Organization" />
      <StructuredData type="SiteNavigation" />
      {/* Iter295.13 — Halo âmbar seguindo o mouse. Sem fallback: se o
          chunk falhar a página segue normal, o halo é bônus. */}
      <Suspense fallback={null}>
        <MouseHaloCursor />
      </Suspense>
      <Hero stats={stats} />

      {/* Barra de atalhos rápidos (pílulas) — sticky logo abaixo da dobra;
          cada pílula rola suavemente até a seção ou abre a editoria. */}
      <HomeQuickNav />

      {/* Iter223 — Faixa "Professor(a) do Dia" só aparece quando algum
          docente cadastrado faz aniversário hoje. Auto-hide silencioso. */}
      <ProfessorOfTheDayStrip />

      {/* Promo card "Estude pelo App" — só mobile. Auto-oculta quando
          já está rodando como PWA standalone ou foi dispensado. */}
      <HomeAppPromoCard />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 xl:px-14 2xl:px-20 py-16 md:py-24 space-y-28 md:space-y-36 relative z-10">
        {/* Latest — render skeleton placeholders while stats is loading
            so the page feels alive from the very first paint, then swap
            in the real cards. */}
        {stats === null ? (
          <section id="ultimos-episodios" data-testid="section-latest-skeleton" className="space-y-6 scroll-mt-28">
            <div className="flex items-center gap-3">
              <Flame className="w-5 h-5 text-red-500" />
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-white">Últimos episódios</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <EpisodeCardSkeleton key={i} featured={i < 2} />
              ))}
            </div>
          </section>
        ) : latest.length > 0 && (
          <section id="ultimos-episodios" data-testid="section-latest" className="space-y-6 scroll-mt-28">
            <div className="flex items-center gap-3">
              <Flame className="w-5 h-5 text-red-500" />
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-white">Últimos episódios</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {latest.slice(0, 4).map((ep, idx) => (
                <EpisodeCard key={ep.id} episode={ep} featured={idx < 2} />
              ))}
            </div>
          </section>
        )}

        {/* Ranking — code-split + visibility-gated; never fetches/renders
            until the user scrolls within ~500px of this section. */}
        <LazyMount minHeight={520} testId="lazy-ranking">
          <Suspense fallback={null}>
            <RankingSection />
          </Suspense>
        </LazyMount>

        {/* Creator of the Month — hidden until the audience grows (~6 months in).
            Re-enable by uncommenting the line below. */}
        {/* <CreatorOfTheMonth /> */}

        {/* Empty state — only render AFTER stats has loaded. Without
            this guard the message blinks on every page load while
            `stats === null` (which gives totalEpisodes=0), embarrassingly
            telling visitors that the acervo is empty when it isn't. */}
        {stats !== null && totalEpisodes === 0 && (
          <section data-testid="empty-state" className="border border-dashed border-slate-800 rounded-2xl p-10 md:p-14 text-center space-y-4 bg-slate-900/30">
            <Library className="w-10 h-10 text-sky-400 mx-auto" />
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-white">Seu acervo ainda está vazio</h3>
            <p className="text-slate-400 max-w-xl mx-auto">
              Acesse o painel administrativo e importe automaticamente os episódios das playlists do YouTube — ou cadastre manualmente com classificação por tema.
            </p>
            <div className="flex justify-center gap-3 pt-2">
              <Link to="/admin/login" className="btn-primary" data-testid="empty-admin-cta">
                <Youtube className="w-4 h-4" /> Importar do YouTube
              </Link>
            </div>
          </section>
        )}

        {/* Podcasts — compact showcase grid (covers + name + description
            + link). Replaces the previous 7 stacked shelves with their
            36+ thumbnails which heavily duplicated the "Últimos" and
            "Ranking" sections. Each podcast's own page (/podcast/:slug)
            now owns the full episode list. */}
        {/* Âncora sempre presente para o atalho #podcasts (a seção real é
            lazy-mounted e pode não existir no DOM na hora do clique). */}
        <span id="podcasts" className="block scroll-mt-28" aria-hidden="true" />
        <LazyMount minHeight={720} testId="lazy-podcasts">
          <Suspense fallback={null}>
            <PodcastsShowcase podcasts={podcasts} />
          </Suspense>
        </LazyMount>

        {/* Vídeos de divulgação — coleção institucional (não é podcast
            editorial) + Shorts verticais. Segmento próprio logo após
            "Nossos podcasts", espelhando a página de vídeos. */}
        <span id="divulgacao" className="block scroll-mt-28" aria-hidden="true" />
        <LazyMount minHeight={420} testId="lazy-promo-videos">
          <Suspense fallback={null}>
            <PromoVideosShowcase />
          </Suspense>
        </LazyMount>

        {/* Editorial copy — placed between Podcasts and Authors as a
            framed, slightly smaller text block. Replaces the previous
            "Navegar por tema" cloud which created redundancy with the
            dedicated /temas page. */}
        <LazyMount minHeight={320} testId="lazy-editorial">
          <Suspense fallback={null}>
            <EditorialIntro />
          </Suspense>
        </LazyMount>

        {/* App Hero Banner antes vivia aqui (entre EditorialIntro e
            Authors). Foi promovido ao Footer global como slot
            permanente — assim a chamada de instalação do PWA aparece
            em TODAS as páginas, sem empurrar para baixo os Radares
            e o widget de Concursos. */}

        {/* Authors */}
        <span id="autores" className="block scroll-mt-28" aria-hidden="true" />
        {authors.length > 0 && (
          <LazyMount minHeight={520} testId="lazy-authors">
            <section data-testid="section-authors" className="space-y-6">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-red-500" />
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-white">Autores no Acervo</h2>
              </div>
              <p className="text-base text-slate-300 max-w-2xl leading-relaxed">
                <strong className="text-white">{stats?.authors ?? authors.length}</strong> autores e autoras citados. Clique para ver a coletânea de trabalhos comentados.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {authors.slice(0, 8).map((a) => {
                  const initials = a.name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
                  return (
                    <Link
                      key={a.slug || a.name}
                      to={`/autor/${a.slug || encodeURIComponent(a.name)}`}
                      data-testid={`home-author-${a.slug || a.name}`}
                      className="card-ep p-4 flex flex-col items-center text-center gap-2"
                    >
                      {a.photo ? (
                        <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-red-500/40 shadow-[0_0_14px_rgba(220,38,38,0.35)]">
                          <img src={a.photo} alt={a.name} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center shadow-[0_0_18px_rgba(220,38,38,0.35)]">
                          <span className="font-serif text-sm font-bold text-white">{initials}</span>
                        </div>
                      )}
                      <div className="font-medium text-xs text-white line-clamp-2 leading-tight">{a.name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-sky-400">
                        {a.episode_count} ep.
                      </div>
                    </Link>
                  );
                })}
              </div>
              {(stats?.authors ?? authors.length) > 8 && (
                <div className="text-center pt-4">
                  <Link
                    to="/autores"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-red-600 hover:bg-red-500 text-white font-semibold text-base md:text-lg transition shadow-lg shadow-red-500/20"
                    data-testid="home-authors-all"
                  >
                    Ver todos os {stats?.authors ?? authors.length} autores
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              )}
            </section>
          </LazyMount>
        )}

        {/* Closing: Blog teaser (lazy + visibility-gated, fetches only
            when scrolled near; auto-hides on empty) + invite to share */}
        <span id="blog" className="block scroll-mt-28" aria-hidden="true" />
        <LazyMount minHeight={360} testId="lazy-blog-teaser">
          <Suspense fallback={null}>
            <BlogTeaser limit={3} />
          </Suspense>
        </LazyMount>
        {/* Legal news daily strip — appears below Blog. Auto-hides when
            empty so home stays clean before the first crawl. */}
        <LazyMount minHeight={480} testId="lazy-legal-news-strip">
          <Suspense fallback={null}>
            <HomeLegalNewsStrip />
            <HomeLegalActsStrip />
          </Suspense>
        </LazyMount>
        {/* Concursos abertos esta semana — widget alimentado pelo MCP do
            PCI Concursos. Auto-oculta se o PCI estiver fora do ar ou se
            não houver editais com prazo > 0. Cache servidor 30 min. */}
        <LazyMount minHeight={360} testId="lazy-concursos-abertos">
          <Suspense fallback={null}>
            <HomeConcursosAbertosStrip />
          </Suspense>
        </LazyMount>
        {/* Radar showcase — vitrine dos 4 banners (Constitucional /
            Notícias / Normativo / Doutrinário). Última seção antes de
            AboutShareBlock; comunica em 1 segundo que o JurisTube tem
            quatro produtos editoriais distintos. */}
        <span id="radar" className="block scroll-mt-28" aria-hidden="true" />
        <LazyMount minHeight={520} testId="lazy-radar-showcase">
          <Suspense fallback={null}>
            <HomeRadarShowcase />
          </Suspense>
        </LazyMount>
        {/* Seção "Rede de Cooperação Institucional" — entra logo após o
            grid de Radares, conforme briefing acadêmico. CTA leva para
            /parcerias.
            (2026-02-28) Fix de alinhamento: as margens negativas agora
            batem EXATAMENTE com o padding do container pai
            (`-mx-4 sm:-mx-6 lg:-mx-10 xl:-mx-14 2xl:-mx-20`). Antes
            usávamos -mx-24/-mx-32/-mx-40 que estouravam em 56–160px
            o container e "puxavam" a faixa para fora da coluna das
            demais seções da home. */}
        <section
          className="relative -mx-4 sm:-mx-6 lg:-mx-10 xl:-mx-14 2xl:-mx-20 mt-12 mb-12 border-y border-emerald-500/20 bg-gradient-to-r from-emerald-950/40 via-slate-950 to-emerald-950/40"
          data-testid="home-parcerias-section"
        >
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 xl:px-14 2xl:px-20 py-12 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <div className="flex-1 space-y-3">
              <span className="text-[11px] uppercase tracking-[0.25em] font-semibold text-emerald-300">
                Cooperação Institucional
              </span>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white leading-snug">
                Um laboratório vivo para o ecossistema jurídico
              </h2>
              <p className="text-sm sm:text-base text-slate-300 leading-relaxed max-w-2xl">
                O JurisTube atua como um laboratório vivo de aplicação de novas tecnologias ao ecossistema
                jurídico e pretende estruturar ações de difusão científica em cooperação com órgãos públicos,
                faculdades e terceiro setor.
              </p>
            </div>
            <div className="shrink-0">
              <Link
                to="/parcerias"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-sm font-bold transition shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/40"
                data-testid="home-parcerias-cta"
              >
                Conheça nossa Rede de Cooperação <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
        {/* Vitrine de "mais acessados" da comunidade — top 3 autores e
            top 3 artigos do mês. Vídeos não entram aqui porque o slot
            "Últimos episódios" no topo da home já cumpre esse papel. */}
        <LazyMount minHeight={360} testId="lazy-community-spotlight">
          <Suspense fallback={null}>
            <HomeCommunitySpotlight />
          </Suspense>
        </LazyMount>
        {/* Autores · Ranking Científico · Mais Publicados —
            complementa o "Mais acessados" (ranking INTERNO de acessos)
            com o ranking EXTERNO de publicações indexadas em ORCID +
            Zenodo. Formato texto puro (sem fotos) — não afeta LCP.
            Auto-hide se o endpoint estiver vazio. */}
        <LazyMount minHeight={220} testId="lazy-scientific-ranking-strip">
          <Suspense fallback={null}>
            <HomeScientificRankingStrip />
          </Suspense>
        </LazyMount>
        <Suspense fallback={null}>
          <AboutShareBlock compact />
        </Suspense>
      </div>
    </>
  );
}
